// Relative, non-destructive fabric compensation.
//
// Fabric selection never overwrites the AI/manual per-layer settings. Instead it
// applies a small multiplier on top of whatever each layer resolves to, at
// generation time — mirroring how a digitizer compensates for fabric behaviour:
//   • stretchy / lofty fabrics (poly, fleece, caps) → more pull compensation,
//     slightly looser density, denser underlay
//   • stable fabrics (denim, canvas) → less pull compensation, tighter density
//   • cotton is the neutral baseline (no change)
//
// Factors are relative to the resolved per-layer value (1.0 = unchanged).
export const FABRIC_COMPENSATION = {
  cotton:     { density: 1.0,  pull: 1.0,  underlay: 1.0 },
  poly_blend: { density: 1.0,  pull: 1.15, underlay: 1.0 },
  fleece:     { density: 1.1,  pull: 1.4,  underlay: 0.85 },
  denim:      { density: 0.95, pull: 0.7,  underlay: 1.0 },
  hat:        { density: 1.0,  pull: 1.25, underlay: 0.8 },
};

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export function isFabricNeutral(fabric) {
  const f = FABRIC_COMPENSATION[fabric];
  return !f || (f.density === 1 && f.pull === 1 && f.underlay === 1);
}

/**
 * Walk a parsed (svgson) SVG tree and scale the Ink/Stitch density,
 * pull-compensation and underlay attributes by the fabric factor. Mutates and
 * returns the same tree. Values are clamped to physically sane ranges.
 */
export function applyFabricCompensation(svgJson, fabric) {
  const f = FABRIC_COMPENSATION[fabric];
  if (!svgJson || isFabricNeutral(fabric)) return svgJson;

  const walk = (el) => {
    if (el && el.attributes) {
      const rs = el.attributes["inkstitch:row_spacing_mm"];
      if (rs) el.attributes["inkstitch:row_spacing_mm"] = clamp(parseFloat(rs) * f.density, 0.15, 0.6).toFixed(3);

      const pc = el.attributes["inkstitch:pull_compensation_mm"];
      if (pc) el.attributes["inkstitch:pull_compensation_mm"] = clamp(parseFloat(pc) * f.pull, 0, 1.0).toFixed(3);

      const us = el.attributes["inkstitch:fill_underlay_row_spacing_mm"];
      if (us) el.attributes["inkstitch:fill_underlay_row_spacing_mm"] = clamp(parseFloat(us) * f.underlay, 1.0, 4.0).toFixed(2);
    }
    if (el && Array.isArray(el.children)) el.children.forEach(walk);
  };
  walk(svgJson);
  return svgJson;
}

/** Short human-readable summary of the active adjustment, or null when neutral. */
export function describeFabricCompensation(fabric) {
  const f = FABRIC_COMPENSATION[fabric];
  if (!f || isFabricNeutral(fabric)) return null;
  const parts = [];
  if (f.density !== 1) parts.push(`density ×${f.density}`);
  if (f.pull !== 1) parts.push(`pull-comp ×${f.pull}`);
  if (f.underlay !== 1) parts.push(`underlay ×${f.underlay}`);
  return parts.join(" · ");
}
